import React from 'react';
import { Link } from 'react-router-dom';
import styles from './LegalPages.module.css';

const PrivacyPolicyPage = () => {
  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <Link to="/" className={styles.backLink}>&larr; Back to Home</Link>

        <h1 className={styles.title}>Privacy Policy</h1>
        <p className={styles.effectiveDate}>Effective Date: February 21, 2026</p>

        {/* 1. Introduction */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>1. Introduction & Controller Identity</h2>
          <p className={styles.paragraph}>
            Teachio ("we," "us," or "our") is operated by REEC Holdings. This Privacy Policy explains
            how we collect, use, store, and protect your personal information when you use our
            AI-powered lesson planning service at{' '}
            <a href="https://www.teachio.ai/" className={styles.inlineLink}>www.teachio.ai</a>.
          </p>
          <p className={styles.paragraph}>
            For the purposes of the EU General Data Protection Regulation (GDPR), the data controller is:
          </p>
          <div className={styles.contactInfo}>
            <p>
              REEC Holdings<br />
              4933 W 106th PL.<br />
              Oak Lawn, IL 60453<br />
              Email: privacy@teachio.ai
            </p>
          </div>
        </div>

        {/* 2. Information We Collect */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>2. Information We Collect</h2>

          <h3 className={styles.subsectionTitle}>Account Information</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <strong>Email address</strong> &mdash; encrypted at rest using AES-256-GCM encryption with a
              unique initialization vector per record. We use an HMAC-SHA256 blind index for email lookups,
              meaning your plaintext email is never stored directly in our database.
            </li>
            <li className={styles.listItem}>
              <strong>Password</strong> &mdash; hashed using bcrypt with 12 salt rounds. We never store
              your plaintext password.
            </li>
            <li className={styles.listItem}>
              <strong>Display name</strong> &mdash; optional, used to personalize your experience.
            </li>
          </ul>

          <h3 className={styles.subsectionTitle}>Educational Content</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>Lesson plans (title, objectives, procedures, assessments, materials, notes)</li>
            <li className={styles.listItem}>Classes (grade level, subject, state, schedule, calendar settings)</li>
            <li className={styles.listItem}>Units and scope & sequence data</li>
            <li className={styles.listItem}>Standards alignment selections</li>
            <li className={styles.listItem}>Custom non-school day entries</li>
          </ul>

          <h3 className={styles.subsectionTitle}>Conversation Data</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Messages exchanged with our AI assistant are stored to maintain conversation context. Conversation
              history is limited to 20 messages per session and is associated with your user account.
            </li>
          </ul>

          <h3 className={styles.subsectionTitle}>Technical Data</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <strong>IP address</strong> &mdash; used solely for rate limiting to prevent abuse.
              We do not log or store IP addresses beyond the duration of active rate-limit windows.
            </li>
            <li className={styles.listItem}>
              <strong>localStorage preferences</strong> &mdash; such as your calendar weekend visibility
              setting, stored locally in your browser only.
            </li>
          </ul>

          <p className={styles.paragraph}>
            We do <strong>not</strong> collect analytics data, use tracking pixels, fingerprint your
            browser, or run any third-party analytics services.
          </p>
        </div>

        {/* 3. How We Use Your Information */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>3. How We Use Your Information</h2>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Purpose</th>
                  <th>GDPR Legal Basis</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Provide and operate the lesson planning service</td>
                  <td>Contract performance (Art. 6(1)(b))</td>
                </tr>
                <tr>
                  <td>Authenticate your account and secure your data</td>
                  <td>Contract performance (Art. 6(1)(b))</td>
                </tr>
                <tr>
                  <td>Generate AI-powered lesson plans and recommendations</td>
                  <td>Contract performance (Art. 6(1)(b))</td>
                </tr>
                <tr>
                  <td>Rate-limit API requests to prevent abuse</td>
                  <td>Legitimate interest (Art. 6(1)(f))</td>
                </tr>
                <tr>
                  <td>Send service-related communications (if enabled)</td>
                  <td>Consent (Art. 6(1)(a))</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 4. Third-Party Data Sharing */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>4. Third-Party Data Sharing</h2>
          <p className={styles.paragraph}>
            We share data with the following service providers, strictly as necessary to operate Teachio:
          </p>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Purpose</th>
                  <th>Data Shared</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Anthropic</td>
                  <td>AI lesson plan generation</td>
                  <td>Chat messages, lesson content, class context (grade, subject, standards). We do <strong>not</strong> send your email, password, or user ID to Anthropic.</td>
                </tr>
                <tr>
                  <td>Neon</td>
                  <td>Database hosting</td>
                  <td>All stored data (encrypted at rest as described above)</td>
                </tr>
                <tr>
                  <td>Render</td>
                  <td>Application hosting</td>
                  <td>Server traffic necessary to serve the application</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className={styles.paragraph}>
            We do <strong>not</strong> sell your personal data. We do <strong>not</strong> share data
            with advertisers. We do <strong>not</strong> use your data for ad targeting. We have no
            analytics or tracking services.
          </p>
        </div>

        {/* 5. Data Storage & Security */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>5. Data Storage & Security</h2>
          <p className={styles.paragraph}>We implement multiple layers of security:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <strong>Encryption at rest</strong> &mdash; user emails are encrypted with AES-256-GCM,
              each with a unique initialization vector.
            </li>
            <li className={styles.listItem}>
              <strong>Password hashing</strong> &mdash; bcrypt with 12 salt rounds.
            </li>
            <li className={styles.listItem}>
              <strong>Authentication</strong> &mdash; JWT tokens with 24-hour expiry.
            </li>
            <li className={styles.listItem}>
              <strong>Security headers</strong> &mdash; Helmet middleware (CSP, HSTS, X-Frame-Options).
            </li>
            <li className={styles.listItem}>
              <strong>CORS protection</strong> &mdash; restricted to allowed origins in production.
            </li>
            <li className={styles.listItem}>
              <strong>Rate limiting</strong> &mdash; authentication (5 per 15 min), general API (100 per min),
              chat (20 per min).
            </li>
            <li className={styles.listItem}>
              <strong>Error masking</strong> &mdash; internal errors are never leaked to the client in production.
            </li>
          </ul>
        </div>

        {/* 6. Data Retention */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>6. Data Retention</h2>
          <p className={styles.paragraph}>
            Your data is retained for as long as your account is active. When you delete your account,
            all associated data is permanently removed, including your lessons, classes, units,
            conversation history, and account information. This deletion is transactional and complete.
          </p>
        </div>

        {/* 7. Your Rights */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>7. Your Rights</h2>

          <h3 className={styles.subsectionTitle}>Under GDPR (EU/EEA Users)</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <strong>Right of access</strong> &mdash; request a copy of your personal data.
            </li>
            <li className={styles.listItem}>
              <strong>Right to rectification</strong> &mdash; correct inaccurate data (you can update
              your name and password directly in your account settings).
            </li>
            <li className={styles.listItem}>
              <strong>Right to erasure</strong> &mdash; delete your account and all associated data.
            </li>
            <li className={styles.listItem}>
              <strong>Right to data portability</strong> &mdash; receive your data in a structured format.
              You can download all your data as a JSON file from your{' '}
              <Link to="/account" className={styles.inlineLink}>Account Settings</Link> page
              at any time.
            </li>
            <li className={styles.listItem}>
              <strong>Right to withdraw consent</strong> &mdash; where processing is based on consent
              (e.g., email communications), you may withdraw at any time.
            </li>
            <li className={styles.listItem}>
              <strong>Right to lodge a complaint</strong> &mdash; you may file a complaint with your
              local data protection supervisory authority.
            </li>
          </ul>

          <h3 className={styles.subsectionTitle}>Under CalOPPA (California Users)</h3>
          <p className={styles.paragraph}>
            <strong>Do Not Track (DNT) disclosure:</strong> Teachio does not track users across
            third-party websites. We do not use cookies, analytics, or tracking technologies. We honor
            DNT signals by default because we simply do not track.
          </p>
        </div>

        {/* 8. Browser Storage */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>8. Browser Storage</h2>
          <p className={styles.paragraph}>
            Teachio does <strong>not</strong> use cookies. Instead, we use browser localStorage for
            two purposes:
          </p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <strong>Authentication token (JWT)</strong> &mdash; keeps you signed in for up to 24 hours.
            </li>
            <li className={styles.listItem}>
              <strong>Calendar preference</strong> &mdash; remembers your weekend visibility setting.
            </li>
          </ul>
          <p className={styles.paragraph}>
            localStorage data never leaves your browser and is not sent to third parties. For more
            details, see our <Link to="/cookies" className={styles.inlineLink}>Cookie Policy</Link>.
          </p>
        </div>

        {/* 9. Children's Privacy */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>9. Children's Privacy</h2>
          <p className={styles.paragraph}>
            Teachio is designed for educators aged 18 and older. We do not knowingly collect information
            from anyone under 18. If we learn that a user is under 18, we will promptly delete their
            account and all associated data. Student data is never collected directly &mdash; Teachio is
            a tool for teachers to create lesson plans, not a student-facing application.
          </p>
        </div>

        {/* 10. International Transfers */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>10. International Transfers</h2>
          <p className={styles.paragraph}>
            Teachio is hosted in the United States. If you access the service from outside the US,
            your data will be transferred to and processed in the US. By using Teachio, you consent
            to this transfer. We protect your data with the security measures described in Section 5
            regardless of where you are located.
          </p>
        </div>

        {/* 11. Changes */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>11. Changes to This Policy</h2>
          <p className={styles.paragraph}>
            We may update this Privacy Policy from time to time. If we make material changes, we will
            notify you by posting a notice on the service. The "Effective Date" at the top of this
            page indicates when this policy was last revised. Your continued use of Teachio after
            changes constitutes acceptance of the updated policy.
          </p>
        </div>

        {/* 12. Contact */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>12. Contact Information</h2>
          <p className={styles.paragraph}>
            If you have questions about this Privacy Policy or wish to exercise your data rights,
            contact us:
          </p>
          <div className={styles.contactInfo}>
            <p>
              REEC Holdings<br />
              4933 W 106th PL.<br />
              Oak Lawn, IL 60453<br />
              Email: privacy@teachio.ai
            </p>
          </div>
        </div>

        {/* Cross-links */}
        <div className={styles.legalNav}>
          <Link to="/terms" className={styles.legalNavLink}>Terms of Service</Link>
          <Link to="/cookies" className={styles.legalNavLink}>Cookie Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
